import { Capacitor } from "@capacitor/core";
import { LocalNotifications } from "@capacitor/local-notifications";
import { api } from "@/lib/http";

/**
 * Native (iOS/Android) reminder scheduling via local notifications.
 * Mirrors the server-side web-push reminders, because web push does not
 * work inside the Capacitor WebView. Re-synced on app start, language
 * change and whenever notification prefs are saved.
 */
const TEXTS = {
  de: {
    meals: { colazione: "Frühstückszeit ☕", spuntino: "Snack-Zeit 🍎", pranzo: "Mittagessen 🍽️", merenda: "Nachmittagssnack 🥨", cena: "Abendessen 🍽️" },
    mealBody: "Vergiss nicht, deine Mahlzeit zu loggen!",
    waterTitle: "Nicht vergessen zu trinken 💧", waterBody: "Ein Glas Wasser jetzt tut dir gut",
    eveningTitle: "🌙 Dein Tagesrückblick", eveningBody: "Kalorien, Makros & Streak — öffne die App für dein Abend-Résumé.",
    trainingTitle: "🏋️ Hast du heute trainiert?", trainingBody: "Trag dein Training ein — dauert 5 Sekunden.",
    weighTitle: "⚖️ Wiegen nicht vergessen!", weighBody: "Stell dich kurz auf die Waage und trag dein Gewicht ein — dauert 10 Sekunden.",
    supTitle: "💊 Supplement-Erinnerung", supBody: "Zeit für dein {name}",
    planTitle: "🏋️ Heute: {name} – bereit?", planBody: "{n} Übungen warten. Tippen und direkt loslegen.",
  },
  it: {
    meals: { colazione: "È l'ora della colazione ☕", spuntino: "Spuntino! 🍎", pranzo: "È l'ora del pranzo 🍽️", merenda: "Merenda! 🥨", cena: "È l'ora della cena 🍽️" },
    mealBody: "Non dimenticare di registrare il pasto!",
    waterTitle: "Non dimenticare di bere 💧", waterBody: "Un bicchiere d'acqua adesso ti fa bene",
    eveningTitle: "🌙 Il tuo riepilogo serale", eveningBody: "Calorie, macro e streak — apri l'app per il tuo résumé.",
    trainingTitle: "🏋️ Hai fatto l'allenamento?", trainingBody: "Segna se ti sei allenato oggi — bastano 5 secondi.",
    weighTitle: "⚖️ Giorno della bilancia!", weighBody: "Sali sulla bilancia e registra il peso — bastano 10 secondi.",
    supTitle: "💊 Promemoria integratore", supBody: "È l'ora di {name}",
    planTitle: "🏋️ Oggi: {name} – pronto?", planBody: "{n} esercizi ti aspettano. Tocca e inizia subito.",
  },
  en: {
    meals: { colazione: "Breakfast time ☕", spuntino: "Snack time 🍎", pranzo: "Lunch time 🍽️", merenda: "Afternoon snack 🥨", cena: "Dinner time 🍽️" },
    mealBody: "Don't forget to log your meal!",
    waterTitle: "Don't forget to drink 💧", waterBody: "A glass of water right now will do you good",
    eveningTitle: "🌙 Your daily recap", eveningBody: "Calories, macros & streak — open the app for your evening recap.",
    trainingTitle: "🏋️ Did you train today?", trainingBody: "Log today's workout — takes 5 seconds.",
    weighTitle: "⚖️ Weigh-in day!", weighBody: "Step on the scale and log your weight — takes 10 seconds.",
    supTitle: "💊 Supplement reminder", supBody: "Time for your {name}",
    planTitle: "🏋️ Today: {name} – ready?", planBody: "{n} exercises are waiting. Tap to start right away.",
  },
  fr: {
    meals: { colazione: "C'est l'heure du petit-déjeuner ☕", spuntino: "Collation 🍎", pranzo: "C'est l'heure du déjeuner 🍽️", merenda: "Goûter 🥨", cena: "C'est l'heure du dîner 🍽️" },
    mealBody: "N'oublie pas d'enregistrer ton repas !",
    waterTitle: "N'oublie pas de boire 💧", waterBody: "Un verre d'eau maintenant te fera du bien",
    eveningTitle: "🌙 Ton récap du jour", eveningBody: "Calories, macros et streak — ouvre l'app pour ton résumé du soir.",
    trainingTitle: "🏋️ As-tu fait ta séance aujourd'hui ?", trainingBody: "Enregistre ta séance — 5 secondes suffisent.",
    weighTitle: "⚖️ Jour de pesée !", weighBody: "Monte sur la balance et note ton poids — 10 secondes suffisent.",
    supTitle: "💊 Rappel supplément", supBody: "C'est l'heure de {name}",
    planTitle: "🏋️ Aujourd'hui : {name} – prêt·e ?", planBody: "{n} exercices t'attendent. Appuie pour démarrer.",
  },
};

const MEAL_IDS = { colazione: 101, spuntino: 102, pranzo: 103, merenda: 104, cena: 105 };
// 2-letter profile codes → Capacitor weekday (1=Sunday..7=Saturday)
const DOW_TO_CAP = { Su: 1, Mo: 2, Tu: 3, We: 4, Th: 5, Fr: 6, Sa: 7 };
const ISO_TO_CAP = (iso) => (iso % 7) + 1; // ISO 1=Mon..7=Sun → cap 2..7,1

const hm = (s, fallback) => {
  const [h, m] = String(s || fallback).split(":").map((x) => parseInt(x, 10));
  return { hour: isNaN(h) ? 8 : h, minute: isNaN(m) ? 0 : m };
};

export async function syncNativeReminders(lang = "de") {
  if (!Capacitor.isNativePlatform()) return false;
  try {
    let perm = await LocalNotifications.checkPermissions();
    if (perm.display !== "granted") perm = await LocalNotifications.requestPermissions();
    if (perm.display !== "granted") return false;

    const { data: prefs } = await api.get("/client/notification-prefs");
    const L = TEXTS[lang] || TEXTS.de;

    const pending = await LocalNotifications.getPending();
    if (pending.notifications?.length) {
      await LocalNotifications.cancel({ notifications: pending.notifications.map((n) => ({ id: n.id })) });
    }

    const list = [];

    if (prefs.enabled_meals) {
      for (const [meal, when] of Object.entries(prefs.meal_times || {})) {
        if (!MEAL_IDS[meal] || !when) continue;
        list.push({
          id: MEAL_IDS[meal],
          title: L.meals[meal] || meal,
          body: L.mealBody,
          schedule: { on: hm(when, "12:00"), allowWhileIdle: true },
          extra: { url: `/app/pasto?meal_type=${meal}` },
        });
      }
    }

    if (prefs.enabled_water) {
      const s = hm(prefs.water_start, "08:00");
      const e = hm(prefs.water_end, "20:00");
      const interval = Math.max(30, parseInt(prefs.water_interval_min, 10) || 120);
      let minutes = s.hour * 60 + s.minute;
      const end = e.hour * 60 + e.minute;
      let i = 0;
      while (minutes <= end && i < 24) {
        list.push({
          id: 200 + i,
          title: L.waterTitle,
          body: L.waterBody,
          schedule: { on: { hour: Math.floor(minutes / 60), minute: minutes % 60 }, allowWhileIdle: true },
          extra: { url: "/app#water" },
        });
        minutes += interval;
        i += 1;
      }
    }

    if (prefs.enabled_training) {
      try {
        const { data: profile } = await api.get("/client/profile");
        const days = Array.isArray(profile?.training_days) ? profile.training_days : [];
        const t = hm(prefs.training_reminder_time, "19:30");
        days.forEach((code) => {
          const wd = DOW_TO_CAP[code];
          if (!wd) return;
          list.push({
            id: 300 + wd,
            title: L.trainingTitle,
            body: L.trainingBody,
            schedule: { on: { weekday: wd, hour: t.hour, minute: t.minute }, allowWhileIdle: true },
            extra: { url: "/app/allenamenti?openLog=1" },
          });
        });
      } catch { /* profile fetch failed — skip training reminders */ }
    }

    if (prefs.enabled_plan !== false) {
      try {
        const { data: plans } = await api.get("/exercises/plans");
        const t = hm(prefs.plan_reminder_time, "08:00");
        (plans || []).slice(0, 8).forEach((p, i) => {
          (p.days || []).forEach((iso) => {
            const wd = ISO_TO_CAP(parseInt(iso, 10) || 1);
            list.push({
              id: 700 + i * 8 + wd,
              title: L.planTitle.replace("{name}", p.name),
              body: L.planBody.replace("{n}", String((p.items || []).length)),
              schedule: { on: { weekday: wd, hour: t.hour, minute: t.minute }, allowWhileIdle: true },
              extra: { url: `/app/allenamenti?startPlan=${p.plan_id}` },
            });
          });
        });
      } catch { /* plans fetch failed — skip */ }
    }

    if (prefs.enabled_evening) {
      list.push({
        id: 400,
        title: L.eveningTitle,
        body: L.eveningBody,
        schedule: { on: hm(prefs.evening_time, "21:00"), allowWhileIdle: true },
        extra: { url: "/app/riepilogo-serale" },
      });
    }

    if (prefs.enabled_weigh !== false) {
      const t = hm(prefs.weigh_time, "08:00");
      (prefs.weigh_days || [1]).forEach((iso) => {
        const wd = ISO_TO_CAP(parseInt(iso, 10) || 1);
        list.push({
          id: 500 + wd,
          title: L.weighTitle,
          body: L.weighBody,
          schedule: { on: { weekday: wd, hour: t.hour, minute: t.minute }, allowWhileIdle: true },
          extra: { url: "/app/progressi" },
        });
      });
    }

    // Supplement reminders — native mirror of the server-side push logic
    // (web push doesn't reach the Capacitor WebView). IDs 600+.
    try {
      const { data: sups } = await api.get("/client/supplements");
      (sups || [])
        .filter((s) => s && s.enabled && s.time_hm)
        .slice(0, 6)
        .forEach((s, i) => {
          const st = hm(s.time_hm, "09:00");
          const title = L.supTitle;
          const body = L.supBody.replace("{name}", (s.name || "Supplement").trim());
          const extra = { url: "/app/profilo#supplements" };
          const days = Array.isArray(s.days_of_week) && s.days_of_week.length ? s.days_of_week : [1, 2, 3, 4, 5, 6, 7];
          if (days.length === 7) {
            list.push({
              id: 600 + i * 8,
              title, body,
              schedule: { on: { hour: st.hour, minute: st.minute }, allowWhileIdle: true },
              extra,
            });
          } else {
            days.forEach((iso) => {
              const wd = ISO_TO_CAP(parseInt(iso, 10) || 1);
              list.push({
                id: 600 + i * 8 + wd,
                title, body,
                schedule: { on: { weekday: wd, hour: st.hour, minute: st.minute }, allowWhileIdle: true },
                extra,
              });
            });
          }
        });
    } catch { /* supplements fetch failed — skip */ }

    if (list.length) await LocalNotifications.schedule({ notifications: list });
    return true;
  } catch {
    return false;
  }
}

// Tapping a native notification deep-links into the right page.
let tapListenerAdded = false;
export function initNativeReminderTapListener(navigate) {
  if (!Capacitor.isNativePlatform() || tapListenerAdded) return;
  tapListenerAdded = true;
  LocalNotifications.addListener("localNotificationActionPerformed", (event) => {
    const url = event?.notification?.extra?.url;
    if (url && typeof url === "string" && url.startsWith("/")) navigate(url);
  });
}
